const STORAGE_PREFIX = "wa_chat_scroll_";
const memoryCache = new Map();

const buildKey = (userId, contactId) =>
  `${STORAGE_PREFIX}${userId}_${contactId}`;

const normalizeScrollState = ({
  distanceFromBottom,
  atBottom,
  currentPage,
}) => ({
  distanceFromBottom: Math.max(0, distanceFromBottom),
  atBottom: Boolean(atBottom),
  currentPage: Math.max(1, currentPage || 1),
});

const parseScrollState = (parsed) => {
  if (
    typeof parsed?.distanceFromBottom !== "number" ||
    typeof parsed?.atBottom !== "boolean"
  ) {
    return null;
  }

  return normalizeScrollState(parsed);
};

export const saveChatScrollPosition = (
  userId,
  contactId,
  { distanceFromBottom, atBottom, currentPage }
) => {
  if (!userId || !contactId) {
    return;
  }

  const state = normalizeScrollState({
    distanceFromBottom,
    atBottom,
    currentPage,
  });
  const key = buildKey(userId, contactId);
  memoryCache.set(key, state);

  try {
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Ignore quota / private-mode errors.
  }
};

const readStoredState = (key) => {
  if (memoryCache.has(key)) {
    return memoryCache.get(key);
  }

  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = parseScrollState(JSON.parse(raw));
    if (parsed) {
      memoryCache.set(key, parsed);
    }
    return parsed;
  } catch {
    return null;
  }
};

export const loadChatScrollPosition = (userId, contactId, legacyUserId) => {
  if (!contactId) {
    return null;
  }

  const keys = [];
  if (userId) {
    keys.push(buildKey(userId, contactId));
  }
  if (legacyUserId && legacyUserId !== userId) {
    keys.push(buildKey(legacyUserId, contactId));
  }

  for (const key of keys) {
    const parsed = readStoredState(key);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

export const clearChatScrollPosition = (userId, contactId) => {
  if (!userId || !contactId) {
    return;
  }

  const key = buildKey(userId, contactId);
  memoryCache.delete(key);

  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore private-mode errors.
  }
};
